import { CalendarOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Layout, Modal, PageHeader, Row, Select, Space, Table, Tag, Typography } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import moment from 'moment';
import { CSSProperties, useCallback, useEffect, useState } from 'react';
import Countdown from 'react-countdown';
import { Field, Grid, User } from '../../models';
import { apiClient, apiErrorMessage } from '../../utils';
import { CellEditPage, RowCardPage } from '../grids';

export function TasksPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<any[]>();
  const [changeCount, setChangeCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<any>(undefined);
  const [rowCardModalOpen, setRowCardModalOpen] = useState<any>();
  const [cellEditModalOpen, setCellEditModalOpen] = useState<any>();

  const [tableCurrent, setTableCurrent] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(25);
  const [tableTotal, setTableTotal] = useState(0);

  const loadTasks = useCallback(() => {
    let offset = (tableCurrent - 1) * tablePageSize;
    offset = offset < 0 ? 0 : offset;

    let queryString: string | undefined = undefined;
    const queryArray: string[] = [`limit=${tablePageSize}`, `offset=${offset}`];

    if (filterValues) {
      const filterKeys = Object.keys(filterValues);
      for (let i = 0; i < filterKeys.length; i++) {
        const key = filterKeys[i];
        const value = filterValues[key];
        if (value) {
          queryArray.push(`${key}=${value}`);
        }
      }
    }

    queryString = queryArray.join('&');

    let url = 'tasks';
    if (queryString) {
      url += '?' + queryString;
    }

    setLoading(true);
    apiClient
      .get(url)
      .then(response => {
        setTasks(response.data.tasks);
        setTableTotal(response.data.count);
        setLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setLoading(false);
      });
  }, [filterValues, tableCurrent, tablePageSize]);

  const loadTaskCounts = useCallback(() => {
    let url = 'tasks/counts';
    if (filterValues?.userId) {
      url += '?userId=' + filterValues.userId;
    }

    apiClient
      .get(url)
      .then(response => {
        setChangeCount(response.data.cellChangeRequestsCount);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  }, [filterValues]);

  useEffect(() => {
    loadTaskCounts();
    loadTasks();
  }, [filterValues, loadTaskCounts, loadTasks]);

  const onTableChange = (pagination: any) => {
    setTableCurrent(pagination.current);
    setTablePageSize(pagination.pageSize);
  };

  const onFiltersToggle = () => {
    setFiltersOpen(!filtersOpen);
  };

  const onFiltersChange = (filterValues: any) => {
    setFilterValues(filterValues);
  };

  const onRefreshClick = () => {
    loadTaskCounts();
    loadTasks();
  };

  const openRowCardModal = (grid: Grid, rowId: string) => {
    setRowCardModalOpen({ grid, rowId });
  };

  const closeRowCardModal = () => {
    setRowCardModalOpen(undefined);
  };

  const onRowSaved = () => {
    loadTaskCounts();
    loadTasks();
  };

  const onCellSaved = () => {
    closeCellEditModal();
    loadTaskCounts();
    loadTasks();
  };

  const openCellEditModal = (grid: Grid, field: Field, rowId: string) => {
    setCellEditModalOpen({ grid, gridId: grid.id, field, rowId });
  };

  const closeCellEditModal = () => {
    setCellEditModalOpen(undefined);
  };

  const tableColumns = [
    {
      title: 'Hedef tarih',
      dataIndex: ['data', 'request', 'dueDate'],
      key: 'dueDate',
      render: (dueDate: Date) => <Text>{moment(dueDate).format('DD.MM.yyyy HH:mm')}</Text>,
    },
    {
      title: 'Kalan süre',
      dataIndex: ['data', 'request', 'dueDate'],
      key: 'timeLeft',
      render: (dueDate: Date, task: any) => {
        const request = task.data.request;
        if (request.status === 'Pending') {
          const passed = moment.duration(moment(dueDate).diff(moment(new Date()))).asMilliseconds() < 0;
          return (
            <>
              {passed ? (
                <Text type="danger">
                  <Countdown
                    date={dueDate}
                    overtime={true}
                    daysInHours={true}
                    renderer={props => `${props.days * 24 + props.hours} saat ${props.minutes} dakika gecikme`}
                  />
                </Text>
              ) : (
                <Text>
                  <Countdown date={dueDate} overtime={true} daysInHours={true} />
                </Text>
              )}
            </>
          );
        } else {
          return (
            <>
              {request.status === 'Approved' && <Text type="success">Onaylandı</Text>}
              {request.status === 'Rejected' && <Text type="danger">Onaylanmadı</Text>}
              <br />
              <Text>{request.actionPerformedBy?.fullName}</Text>
              <br />
              <Text type="secondary">{moment(request.updatedAt).format('DD.MM.yyyy HH:mm')}</Text>
            </>
          );
        }
      },
    },
    {
      title: 'İşlem',
      dataIndex: 'taskType',
      key: 'taskType',
      render: (taskType: string) => <>{taskType === 'GridCellChangeRequest' && <Tag color="#faad14">Veri değişikliği onayı</Tag>}</>,
    },
    {
      title: 'Önem',
      dataIndex: 'taskType',
      key: 'priority',
      render: (_: string, task: any) => {
        const priority: number = task?.data?.field?.priority || task?.data?.grid?.priority;
        const priorityLabel = priority === 3 ? '!!!' : priority === 2 ? '!!' : '!';

        return <Tag color="red">{priorityLabel}</Tag>;
      },
    },
    {
      title: 'Veri seti',
      dataIndex: 'taskType',
      key: 'requestPath',
      render: (taskType: string, task: any) => {
        let path = '';
        let rowId = '';
        path = `${task.data.field.grid.name} / ${task.data.field.name}`;
        rowId = task.data.cell.rowId;

        return (
          <Space direction="vertical" size="small">
            <Text strong>{path}</Text>
            <Text type="secondary" copyable={{ text: rowId }}>
              Kayıt ID: {rowId}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Değişiklik',
      dataIndex: 'taskType',
      key: 'summary',
      render: (_: string, task: any) => <RequestSummary task={task} />,
    },
    {
      title: 'Oluşturan',
      dataIndex: 'taskType',
      key: 'createdBy',
      render: (taskType: string, task: any) => (
        <div>
          {task.data.request.integrationExecution ? (
            <span style={{ textDecoration: task.data.request.integrationExecution.integration.deletedAt ? 'line-through' : 'inherit' }}>
              {task.data.request.integrationExecution.integration.name}
            </span>
          ) : (
            <span>{task.data.request.createdBy?.fullName || 'Otomatik'}</span>
          )}
          <br />
          <Text type="secondary">{moment(task.data.request.createdAt).format('DD.MM.yyyy HH:mm')}</Text>
        </div>
      ),
    },
    {
      title: null,
      dataIndex: 'taskType',
      key: 'actions',
      render: (taskType: string, task: any) => (
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={() => openCellEditModal(task.data.field.grid, task.data.field, task.data.cell.rowId)}>Detaylar</Button>
            <Button onClick={() => openRowCardModal(task.data.field.grid, task.data.cell.rowId)}>Kart</Button>
          </Space>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <CalendarOutlined /> Görevler
          </>
        }
        subTitle={
          <>
            <span>Değişiklik talebi: </span>
            <span style={{ color: '#faad14', fontWeight: 'bold' }}>{changeCount}</span>
          </>
        }
        extra={[
          <Button key="filters" onClick={onFiltersToggle} icon={<FilterOutlined />}>
            Filtreleri {filtersOpen ? 'gizle' : 'göster'}
          </Button>,
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
        ]}
      />
      <Content>
        <FiltersForm hidden={!filtersOpen} onChange={onFiltersChange} />
        <Table
          rowKey={(row: any) => row.data.request.id}
          columns={tableColumns}
          dataSource={tasks}
          loading={loading}
          scroll={{ x: '100%' }}
          pagination={{
            current: tableCurrent,
            total: tableTotal,
            pageSize: tablePageSize,
            pageSizeOptions: ['25', '50', '100'],
            position: ['bottomCenter'],
            showTotal: (total, range) => `Toplam ${total} kayıt. ${range[0]}-${range[1]} arası gösteriliyor.`,
            showSizeChanger: true,
          }}
          onChange={onTableChange}
        />
      </Content>
      <Modal
        title="Kayıt detayları"
        visible={rowCardModalOpen}
        footer={null}
        onCancel={closeRowCardModal}
        destroyOnClose={true}
        width={'50%'}
      >
        <RowCardPage grid={rowCardModalOpen?.grid} rowId={rowCardModalOpen?.rowId} onSaved={onRowSaved} />
      </Modal>
      <Modal
        title="Veri detayları"
        visible={cellEditModalOpen}
        footer={null}
        onCancel={closeCellEditModal}
        destroyOnClose={true}
        width={'90%'}
      >
        <CellEditPage
          gridId={cellEditModalOpen?.gridId}
          rowId={cellEditModalOpen?.rowId}
          field={cellEditModalOpen?.field}
          allowRecordWithErrors={rowCardModalOpen?.grid?.allowRecordWithErrors}
          onSaved={onCellSaved}
        />
      </Modal>
    </>
  );
}

function RequestSummary(props: { task?: any }) {
  const { Text } = Typography;

  const colStyle: CSSProperties = {
    textAlign: 'center',
    border: '1px solid #e1e1e1',
  };

  const request = props.task?.data.request;
  if (!request) {
    return null;
  }
  const fieldIds = request.sourceRow.grid.fields.filter((m: any) => m.isIdentifier).map((m: any) => m.id);
  const row1Cells = request.sourceRow.cells;
  const row1Value = fieldIds.map((f: string) => row1Cells[f].value).join(' - ');
  const row2Cells = request.duplicateRow.cells;
  const row2Value = fieldIds.map((f: string) => row2Cells[f].value).join(' - ');
  return (
    <Row gutter={16}>
      <Col span={11} style={colStyle}>
        <Space direction="vertical">
          <Text type="secondary">Kayıt 1</Text>
          <span>{row1Value || '-'}</span>
        </Space>
      </Col>
      <Col span={2} />{' '}
      <Col span={11} style={colStyle}>
        <Space direction="vertical">
          <Text type="secondary">Kayıt 2</Text>
          <span>{row2Value || '-'}</span>
        </Space>
      </Col>
    </Row>
  );
}

function FiltersForm(props: { hidden: boolean; onChange: (filterValues: any) => void }) {
  const { Option } = Select;
  const [form] = useForm();

  const [grids, setGrids] = useState<Grid[]>();
  const [fields, setFields] = useState<Field[]>();
  const [users, setUsers] = useState<User[]>();
  const [gridsLoading, setGridsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  useEffect(() => {
    loadGrids();
    loadUsers();
  }, []);

  const loadGrids = () => {
    setGridsLoading(true);
    apiClient
      .get('grids')
      .then(response => {
        setGrids(response.data as Grid[]);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setGridsLoading(false);
      });
  };

  const loadUsers = () => {
    setUsersLoading(true);
    apiClient
      .get('users?minimal=true')
      .then(response => {
        setUsers(response.data as User[]);
      })
      .catch(() => {})
      .finally(() => {
        setUsersLoading(false);
      });
  };

  const onChange = () => {
    const values = form.getFieldsValue();
    props.onChange(values);
  };

  const onGridChange = () => {
    const values = form.getFieldsValue();
    values.fieldId = undefined;
    form.setFieldsValue(values);
    if (values.gridId) {
      const grid = grids?.find(g => g.id === values.gridId);
      setFields(grid?.fields);
    } else {
      setFields(undefined);
    }
    setFields(undefined);
    props.onChange(values);
  };

  if (props.hidden) {
    return null;
  }

  return (
    <Card style={{ marginBottom: '2rem' }}>
      <Form form={form} layout="inline">
        <Form.Item name="taskType">
          <Select allowClear placeholder="İşlem" style={{ width: '12rem' }} onChange={onChange}>
            <Option value="GridCellChangeRequest">Veri değişikliği onayı</Option>
          </Select>
        </Form.Item>
        <Form.Item name="gridId">
          <Select allowClear loading={gridsLoading} placeholder="Veri seti" style={{ width: '12rem' }} onChange={onGridChange}>
            {grids?.map(g => (
              <Option key={g.id} value={g.id}>
                {g.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="fieldId">
          <Select
            allowClear
            loading={gridsLoading}
            disabled={!fields}
            placeholder="Veri elemanı"
            style={{ width: '12rem' }}
            onChange={onChange}
          >
            {fields?.map(f => (
              <Option key={f.id} value={f.id}>
                {f.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="passedDueDate">
          <Select allowClear placeholder="Kalan süre" style={{ width: '12rem' }} onChange={onChange}>
            <Option value="true">Geçmiş</Option>
            <Option value="false">Geçmemiş</Option>
          </Select>
        </Form.Item>
        <Form.Item name="status">
          <Select allowClear placeholder="Durum" style={{ width: '12rem' }} onChange={onChange}>
            <Option value="all">Tümü</Option>
            <Option value="pending">Bekleyen</Option>
            <Option value="completed">Tamamlanmış</Option>
          </Select>
        </Form.Item>
        {users && (
          <Form.Item name="userId">
            <Select allowClear loading={usersLoading} placeholder="Kullanıcı" style={{ width: '12rem' }} onChange={onChange}>
              {users?.map(u => (
                <Option key={u.id} value={u.id}>
                  {u.fullName}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </Form>
    </Card>
  );
}
